<section class="tc-steps" aria-labelledby="tc-steps-heading">
  <to-section-title
    headingId="tc-steps-heading"
    [title]="'testCases.form.testSteps.blockTitle' | translate"
    [subtitle]="textKey('blockSubtitle') | translate: { count: steps().length }"
  >
    <div right class="tc-steps__actions">
      <button
        mat-stroked-button
        type="button"
        class="tc-steps__library-btn"
        [disabled]="busy() || readOnly()"
        (click)="openLibrary()"
      >
        <mat-icon aria-hidden="true">folder</mat-icon>
        {{ textKey('library') | translate }}
      </button>
      @if (allowModuleEmbed()) {
        <button
          mat-stroked-button
          type="button"
          class="tc-steps__embed-btn"
          data-testid="tf-modul-einbinden"
          [disabled]="busy() || readOnly()"
          (click)="openModuleEmbed()"
        >
          <mat-icon aria-hidden="true">extension</mat-icon>
          {{ 'testCases.form.testSteps.embedModule' | translate }}
        </button>
      }
      <button
        mat-flat-button
        color="primary"
        type="button"
        class="tc-steps__add-btn"
        data-testid="tf-schritt-anlegen"
        [disabled]="busy() || readOnly() || rowOpen()"
        (click)="startDraft()"
      >
        {{ 'testCases.form.testSteps.addStep' | translate }}
      </button>
    </div>
  </to-section-title>

  <lib-modal
    [closeLabel]="'general.close' | translate"
    [title]="'common.unsavedChanges.title' | translate"
    [text]="'testCases.form.testSteps.switchEditorConfirm' | translate"
    [visible]="pendingSwitch() !== null"
    [saveText]="'common.unsavedChanges.leave' | translate"
    [cancelText]="'common.unsavedChanges.stay' | translate"
    (save)="confirmSwitch()"
    (cancel)="stayInEditor()"
    (visibleChange)="!$event && stayInEditor()"
  ></lib-modal>

  <!-- Nur der Testfall-Editor projiziert hier seinen KI-Qualitätscheck, der Modul-Editor nicht. -->
  <ng-content select="[stepsCheck]" />

  <app-test-step-library-dialog
    [projectId]="projectId()"
    [kontext]="kontext()"
    [visible]="showLibrary()"
    (visibleChange)="showLibrary.set($event)"
    (applied)="onLibraryApplied($event)"
  ></app-test-step-library-dialog>

  @if (allowModuleEmbed()) {
    <app-module-embed-dialog
      [projectId]="projectId()"
      [excludeIds]="moduleEmbedExcludeIds()"
      [visible]="showModuleEmbed()"
      (visibleChange)="showModuleEmbed.set($event)"
      (applied)="onModuleEmbedApplied($event)"
    ></app-module-embed-dialog>
  }

  @if (loading()) {
    <div class="tc-steps__loading">
      <mat-spinner diameter="28" />
    </div>
  } @else {
    @if (error()) {
      <p class="tc-steps__error" role="alert">{{ 'testCases.form.testSteps.error' | translate }}</p>
    }

    <div class="tc-steps__tcdl-bar" data-testid="tf-tcdl">
      <button
        type="button"
        role="switch"
        class="tc-steps__tcdl-all"
        data-testid="tcdl-toggle-all"
        [class.tc-steps__tcdl-all--on]="tcdlAll()"
        [attr.aria-checked]="tcdlAll()"
        (click)="toggleTcdlAll()"
      >
        <span class="tc-steps__tcdl-all-track" aria-hidden="true">
          <span class="tc-steps__tcdl-all-thumb"></span>
        </span>
        <span class="tc-steps__tcdl-all-body">
          <span class="tc-steps__tcdl-all-title">{{ 'testSteps.form.tcdlToggleAll' | translate }}</span>
          <span class="tc-steps__tcdl-all-subline">{{ tcdlToggleAllSublineKey() | translate }}</span>
        </span>
      </button>
    </div>

    <div class="tc-steps__table" appScrollContainer role="table" [attr.aria-label]="textKey('tableLabel') | translate">
      <div class="tc-steps__thead" role="row">
        <div class="tc-steps__th tc-steps__th--nr" role="columnheader">
          {{ 'testCases.form.testSteps.numberColumn' | translate }}
        </div>
        <div class="tc-steps__th tc-steps__th--main" role="columnheader">
          {{ 'testCases.form.testSteps.stepColumn' | translate }}
        </div>
        <div class="tc-steps__th tc-steps__th--link" role="columnheader">
          {{ 'testCases.form.testSteps.linkColumn' | translate }}
        </div>
        <div class="tc-steps__th tc-steps__th--maintainer" role="columnheader">
          {{ 'testCases.form.testSteps.maintainerColumn' | translate }}
        </div>
        <div class="tc-steps__th tc-steps__th--actions" role="columnheader">
          {{ 'testCases.form.testSteps.actions' | translate }}
        </div>
      </div>

      @if (tableRows().length > 0) {
        @for (step of tableRows(); track step.id; let i = $index) {
          @if (isModuleRow(step)) {
            <div
              class="tc-steps__row tc-steps__module-row"
              data-testid="tf-modul"
              [class.tc-steps__module-row--list]="isListMode()"
              [attr.data-step-id]="step.id"
              role="row"
            >
              <div class="tc-steps__col tc-steps__col--nr tc-steps__module-nr" role="cell">
                <span class="tc-steps__bubble tc-steps__bubble--module">{{ i + 1 }}</span>
              </div>
              <div class="tc-steps__module-cell" role="cell" aria-colspan="3">
                @if (isListMode()) {
                  <app-step-condition-list
                    class="tc-steps__module-conditions"
                    [conditions]="conditionRows(step)"
                    [scopeId]="step.id"
                    [library]="conditionLibrary()"
                    [disabled]="readOnly()"
                    (conditionsChange)="onConditionListChanged(step, $event)"
                  />
                }
                <button
                  type="button"
                  class="tc-steps__module-toggle"
                  [attr.aria-expanded]="isModuleExpanded(step.id)"
                  [attr.aria-label]="
                    (isModuleExpanded(step.id)
                      ? 'testCases.form.testSteps.moduleCollapse'
                      : 'testCases.form.testSteps.moduleExpand'
                    ) | translate
                  "
                  (click)="toggleModuleExpand(step)"
                >
                  <mat-icon aria-hidden="true">
                    {{ isModuleExpanded(step.id) ? 'expand_more' : 'chevron_right' }}
                  </mat-icon>
                </button>
                <div class="tc-steps__main tc-steps__module-main">
                  <div class="tc-steps__module-head">
                    <mat-icon class="tc-steps__module-icon" aria-hidden="true">extension</mat-icon>
                    <span class="tc-steps__module-tag">{{ 'testCases.form.testSteps.module.tag' | translate }}</span>
                    <span class="tc-steps__module-name">{{ liveModuleTitle(step) }}</span>
                    @if (closesCycle(step)) {
                      <app-recursive-badge />
                      <span class="tc-steps__module-recursive" data-testid="module-step-recursive-call">
                        {{ 'testCases.form.testSteps.moduleRecursiveCall' | translate }}
                      </span>
                    }
                    @if (isModuleUnavailable(step.id)) {
                      <span class="tc-steps__module-state" data-testid="tf-modul-nicht-verfuegbar">
                        {{ 'testCases.form.testSteps.module.unavailable' | translate }}
                      </span>
                    } @else if (isModuleDeprecated(step)) {
                      <span class="tc-steps__module-state" data-testid="tf-modul-veraltet">
                        {{ 'testCases.form.testSteps.module.deprecated' | translate }}
                      </span>
                    }
                    @if (showStepKeys() && step.key; as key) {
                      <app-step-key-pill [key]="key" />
                    }
                    @if (step.calledModuleId && !readOnly()) {
                      <button
                        type="button"
                        class="tc-steps__module-edit-btn"
                        data-testid="tf-modul-bearbeiten"
                        [class.tc-steps__module-edit-btn--no-right]="!canEditModule()"
                        [attr.aria-disabled]="canEditModule() ? null : 'true'"
                        [attr.title]="
                          canEditModule() ? null : ('testCases.form.testSteps.module.editNoRight' | translate)
                        "
                        [disabled]="busy()"
                        (click)="onEditModule(step)"
                      >
                        <mat-icon aria-hidden="true">edit</mat-icon>
                        {{ 'testCases.form.testSteps.edit' | translate }}
                      </button>
                    }
                    @if (liveModuleStepCount(step) !== null) {
                      <span class="tc-steps__module-badge">
                        {{
                          'testCases.form.testSteps.moduleStepCount' | translate: { count: liveModuleStepCount(step) }
                        }}
                      </span>
                    }
                    @for (sheet of linkedSheetsForStep(step); track sheet.id) {
                      <span
                        class="tc-steps__tdb-chip"
                        [class.tc-steps__tdb-chip--restricted]="isChipRestricted(step, sheet)"
                        [attr.data-testid]="'tdb-chip-' + sheet.key"
                        [attr.title]="chipTooltip(step, sheet)"
                      >
                        <mat-icon aria-hidden="true">table_chart</mat-icon>
                        {{ chipLabel(step, sheet) }}
                      </span>
                    }
                    @if (step.calledModuleId) {
                      <button
                        type="button"
                        class="tc-steps__module-open"
                        [attr.aria-label]="'testCases.form.testSteps.openModule' | translate"
                        (click)="openModule(step)"
                      >
                        <mat-icon aria-hidden="true">open_in_new</mat-icon>
                      </button>
                    }
                  </div>
                  @if (tcdlAll() && !isModuleExpanded(step.id)) {
                    <code
                      class="tc-steps__condition-preview tc-steps__module-tcdl"
                      [attr.data-testid]="'module-tcdl-' + step.id"
                    >
                      {{ moduleTcdlPreview(step) }}
                    </code>
                  }
                  @if (isModuleExpanded(step.id)) {
                    <div
                      class="tc-steps__module-steps"
                      [class.tc-steps__module-steps--conditional]="conditionsActive(step)"
                    >
                      @if (conditionsActive(step)) {
                        <div class="tc-steps__module-only-if">{{ 'tdb.condition.onlyIf' | translate }}</div>
                      }
                      <ng-template #tdbPanel>
                        @if (blockRows(step).length > 0) {
                          <div class="tc-steps__tdb-panel" [attr.data-testid]="isListMode() ? 'tf-tdb-auswahl' : null">
                            <app-testdaten-block
                              [moduleTitle]="moduleTitleFor(step)"
                              [rows]="blockRows(step)"
                              [disabled]="readOnly()"
                              [showPreview]="isListMode() || tcdlAll()"
                              [showCondition]="!isListMode()"
                              (overrideChanged)="onBlockOverrideChanged(step, $event)"
                              (overrideReset)="onBlockOverrideReset(step)"
                            />
                          </div>
                        }
                        @if (secretColumnErrorFor(step); as secret) {
                          <p
                            class="tc-steps__secret-error"
                            role="alert"
                            [attr.data-testid]="'tf-secret-column-error-' + step.id"
                          >
                            <span class="tc-steps__secret-error-icon" aria-hidden="true">✕</span>
                            {{
                              'tdb.secretColumnNotEncrypted'
                                | translate: { parameter: secret.field, column: secret.column }
                            }}
                          </p>
                        }
                      </ng-template>
                      @if (!isListMode()) {
                        @if (blockRows(step).length > 0) {
                          <ng-container [ngTemplateOutlet]="tdbPanel" />
                        } @else {
                          <div class="tc-steps__condition-panel" [attr.data-testid]="'step-condition-block-' + step.id">
                            <div class="tc-steps__condition-label">{{ 'tdb.field.condition' | translate }}</div>
                            <app-step-condition-editor
                              [override]="currentOverride(step)"
                              [scopeId]="step.id"
                              [disabled]="readOnly()"
                              (changed)="onConditionChanged(step, $event)"
                            />
                            @if (tcdlAll()) {
                              <code
                                class="tc-steps__condition-preview"
                                [attr.data-testid]="'step-condition-preview-' + step.id"
                              >
                                {{ moduleTcdlPreview(step) }}
                              </code>
                            }
                          </div>
                        }
                      }
                      @if (isModuleLoading(step.id)) {
                        <p class="tc-steps__module-loading" role="status">
                          {{ 'testCases.form.testSteps.module.loading' | translate }}
                        </p>
                        @if (isListMode()) {
                          <ng-container [ngTemplateOutlet]="tdbPanel" />
                        }
                      } @else if (isModuleUnavailable(step.id)) {
                        <p class="tc-steps__module-unavailable" role="alert">
                          {{ 'testCases.form.testSteps.module.unavailableHint' | translate }}
                        </p>
                        @if (isListMode()) {
                          <ng-container [ngTemplateOutlet]="tdbPanel" />
                        }
                      } @else if (moduleLoadFailed(step.id)) {
                        <p class="tc-steps__module-error" role="alert">
                          {{ 'testCases.form.testSteps.moduleLoadError' | translate }}
                          <button type="button" class="tc-steps__module-retry" (click)="retryModuleLoad(step)">
                            {{ 'testCases.form.testSteps.moduleLoadRetry' | translate }}
                          </button>
                        </p>
                        @if (isListMode()) {
                          <ng-container [ngTemplateOutlet]="tdbPanel" />
                        }
                      } @else {
                        <p class="tc-steps__module-readonly-hint">
                          {{ 'testCases.form.testSteps.moduleReadonlyHint' | translate }}
                        </p>
                        @if (isListMode()) {
                          <ng-container [ngTemplateOutlet]="tdbPanel" />
                        }
                        @if (liveModuleSteps(step).length > 0) {
                          <div class="tc-steps__module-table" role="table" [attr.aria-label]="liveModuleTitle(step)">
                            <div class="sr-only" role="row">
                              <span class="sr-only" role="columnheader">
                                {{ 'testCases.form.testSteps.numberColumn' | translate }}
                              </span>
                              <span class="sr-only" role="columnheader">
                                {{ 'testCases.form.testSteps.stepColumn' | translate }}
                              </span>
                            </div>
                            @for (inner of liveModuleSteps(step); track inner.id; let j = $index) {
                              @if (isModuleRow(inner) && inner.calledModuleId) {
                                <div class="tc-steps__module-nested" role="row">
                                  <div role="cell" aria-colspan="2">
                                    <app-module-step-tree
                                      class="tc-steps__module-tree"
                                      [projectId]="projectId()"
                                      [moduleId]="inner.calledModuleId"
                                      [name]="liveModuleTitle(inner)"
                                      [ancestors]="nestedAncestors().get(step.id) ?? []"
                                      [prefix]="moduleStepNumber(i, j)"
                                      [availableSheets]="availableSheets()"
                                      [showStepKeys]="showStepKeys()"
                                      [stepKey]="inner.key"
                                      [readOnly]="readOnly()"
                                      [canEditModule]="canEditModule()"
                                      (editModule)="onNestedModuleEdit($event)"
                                    />
                                  </div>
                                </div>
                              } @else {
                                <div
                                  class="tc-steps__module-substep"
                                  role="row"
                                  [class.tc-steps__module-substep--zebra]="j % 2 === 1"
                                >
                                  <span class="tc-steps__module-substep-nr" role="cell">
                                    {{ moduleStepNumber(i, j) }}
                                  </span>
                                  <div class="tc-steps__module-substep-body" role="cell">
                                    <div class="tc-steps__module-substep-title-row">
                                      <div class="tc-steps__module-substep-title">
                                        {{ inner.title || inner.action || '—' }}
                                      </div>
                                      @if (showStepKeys() && inner.key; as key) {
                                        <app-step-key-pill [key]="key" />
                                      }
                                    </div>
                                    @if (inner.expectedResult) {
                                      <div class="tc-steps__module-substep-expected">{{ inner.expectedResult }}</div>
                                    }
                                  </div>
                                </div>
                              }
                            }
                          </div>
                        } @else {
                          <p class="tc-steps__module-empty">{{ 'testCases.form.testSteps.moduleEmpty' | translate }}</p>
                        }
                      }
                    </div>
                  }
                </div>
              </div>
              <div class="tc-steps__col tc-steps__col--actions" role="cell">
                <button
                  type="button"
                  class="tc-steps__action-btn"
                  [attr.aria-label]="'testCases.form.testSteps.moveUp' | translate"
                  [disabled]="busy() || readOnly() || i === 0"
                  (click)="moveUp(i)"
                >
                  <mat-icon aria-hidden="true">arrow_upward</mat-icon>
                </button>
                <button
                  type="button"
                  class="tc-steps__action-btn"
                  [attr.aria-label]="'testCases.form.testSteps.moveDown' | translate"
                  [disabled]="busy() || readOnly() || i === steps().length - 1"
                  (click)="moveDown(i)"
                >
                  <mat-icon aria-hidden="true">arrow_downward</mat-icon>
                </button>
                <button
                  type="button"
                  class="tc-steps__module-unlink"
                  data-testid="tf-modul-loesen"
                  [disabled]="busy() || readOnly()"
                  (click)="delete(step)"
                >
                  {{ 'testCases.form.testSteps.module.unlink' | translate }}
                </button>
              </div>
            </div>
          } @else if (editingId() === step.id) {
            <div
              class="tc-steps__row tc-steps__row--editing"
              data-testid="tf-schritt"
              [attr.data-step-id]="step.id"
              role="row"
            >
              <div class="tc-steps__col tc-steps__col--nr" role="cell">
                <span class="tc-steps__bubble tc-steps__bubble--editing">{{ i + 1 }}</span>
              </div>
              <div class="tc-steps__edit" role="cell" aria-colspan="4">
                <input
                  #editTitleInput
                  type="text"
                  class="tc-steps__edit-title"
                  [value]="editTitle()"
                  [attr.maxlength]="fieldLengths.NAME_MAX"
                  (input)="onEditTitleInput($event)"
                  (keydown.enter)="saveEdit()"
                  (keydown.escape)="cancelEdit()"
                  [attr.aria-label]="'testCases.form.testSteps.title' | translate"
                  [placeholder]="'testCases.form.testSteps.titlePlaceholder' | translate"
                  required
                  aria-required="true"
                  [attr.aria-invalid]="titleMissing() ? 'true' : null"
                  [attr.aria-describedby]="titleMissing() ? 'tc-steps-edit-required' : null"
                />
                <textarea
                  class="tc-steps__edit-action"
                  rows="2"
                  [value]="editAction()"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  (input)="onEditActionInput($event)"
                  (keydown.escape)="cancelEdit()"
                  [attr.aria-label]="'testCases.form.testSteps.action' | translate"
                  [placeholder]="'testCases.form.testSteps.actionPlaceholder' | translate"
                ></textarea>
                <textarea
                  #editExpectedInput
                  class="tc-steps__edit-expected"
                  rows="2"
                  [value]="editExpected()"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  (input)="onEditExpectedInput($event)"
                  (keydown.escape)="cancelEdit()"
                  [attr.aria-label]="'testCases.form.testSteps.expectedResult' | translate"
                  [placeholder]="'testCases.form.testSteps.expectedResultPlaceholder' | translate"
                  required
                  aria-required="true"
                  [attr.aria-invalid]="expectedMissing() ? 'true' : null"
                  [attr.aria-describedby]="expectedMissing() ? 'tc-steps-edit-required' : null"
                ></textarea>
                @if (requiredMissing()) {
                  <span id="tc-steps-edit-required" class="tc-steps__edit-required" role="alert">
                    {{ 'testCases.form.testSteps.requiredFields' | translate }}
                  </span>
                }
                @if (tcdlAll()) {
                  <span class="tc-steps__edit-tcdl-label">
                    {{ 'testSteps.form.tcdlLabel' | translate }}
                  </span>
                  <app-tcdl-editor
                    size="compact"
                    [projectId]="projectId()"
                    [value]="editTcdl()"
                    [freetext]="editAction()"
                    [maxLength]="fieldLengths.FREETEXT_MAX"
                    [ariaLabel]="'testSteps.form.tcdlEditorAriaLabel' | translate"
                    (valueChange)="onEditTcdlChange($event)"
                    (advanceRequested)="onTcdlAdvance()"
                  />
                }
                <div class="tc-steps__edit-controls">
                  <input
                    #editNotesInput
                    type="text"
                    class="tc-steps__edit-link"
                    [value]="editNotes()"
                    [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                    (input)="onEditNotesInput($event)"
                    (keydown.enter)="saveEdit()"
                    (keydown.escape)="cancelEdit()"
                    [attr.aria-label]="'testCases.form.testSteps.linkColumn' | translate"
                    [placeholder]="'testCases.form.testSteps.linkPlaceholder' | translate"
                  />
                  <span class="tc-steps__edit-spacer"></span>
                  <button type="button" class="tc-steps__edit-btn tc-steps__edit-btn--cancel" (click)="cancelEdit()">
                    {{ 'testCases.form.testSteps.cancelEdit' | translate }}
                  </button>
                  <button
                    type="button"
                    class="tc-steps__edit-btn tc-steps__edit-btn--save"
                    [disabled]="busy()"
                    (click)="saveEdit()"
                  >
                    <mat-icon aria-hidden="true">check</mat-icon>
                    {{ 'testCases.form.testSteps.applyEdit' | translate }}
                  </button>
                </div>
                @if (offeneZeileGemeldet()) {
                  <p class="tc-steps__edit-required" role="alert" data-testid="step-open-error">
                    {{ 'testCases.form.saveBlockedStepOpen' | translate }}
                  </p>
                }
              </div>
            </div>
          } @else {
            <div
              class="tc-steps__row"
              [class.tc-steps__row--zebra]="i % 2 === 1"
              [class.tc-steps__row--ki-offen]="kiBefundeJeSchritt().has(step.id)"
              [class.tc-steps__row--ki-oben]="kiBefundeJeSchritt().has(step.id) || !!kiAenderungen()[step.id]"
              [class.tc-steps__row--ki-neu]="kiNeuId() === step.id"
              data-testid="tf-schritt"
              [attr.data-step-id]="step.id"
              role="row"
            >
              <div class="tc-steps__col tc-steps__col--nr" role="cell">
                <span class="tc-steps__bubble">{{ i + 1 }}</span>
              </div>
              <div class="tc-steps__main" role="cell">
                <div class="tc-steps__title-row">
                  <div class="tc-steps__title">
                    <span
                      [class.tc-steps__ki-feld]="kiFeldGeaendert(step, 'titel')"
                      [textContent]="step.title || step.action || '—'"
                    ></span>
                  </div>
                  @if (showStepKeys() && step.key; as key) {
                    <app-step-key-pill [key]="key" />
                  }
                </div>
                @if (step.title && step.action) {
                  <div class="tc-steps__desc">
                    <span [class.tc-steps__ki-feld]="kiFeldGeaendert(step, 'beschreibung')">{{ step.action }}</span>
                  </div>
                }
                @if (step.expectedResult) {
                  <div class="tc-steps__soll">
                    <span class="tc-steps__soll-label">
                      {{ 'testCases.form.testSteps.expectedPrefix' | translate }}
                    </span>
                    <span [class.tc-steps__ki-feld]="kiFeldGeaendert(step, 'soll')">{{ step.expectedResult }}</span>
                  </div>
                }
                @for (befund of kiBefundeJeSchritt().get(step.id) ?? []; track befund.key) {
                  <app-ki-befund-inline
                    [befund]="befund"
                    [aktuell]="kiAktuellerWert(step, befund)"
                    [readOnly]="readOnly()"
                    (uebernehmen)="kiBefundUebernehmen(befund)"
                    (lassen)="kiBefundLassen(befund)"
                  />
                }
                @for (aenderung of kiAenderungen()[step.id] ?? []; track aenderung.ziel) {
                  <div
                    class="tc-steps__ki-diff"
                    role="region"
                    data-testid="ki-geaendert"
                    [attr.aria-label]="
                      'ki.qualitaet.geaendertRegion'
                        | translate: { feld: ('ki.qualitaet.feld.' + aenderung.feld | translate) }
                    "
                  >
                    <div class="tc-steps__ki-diff-kopf">
                      <app-ki-mark [size]="13" />
                      {{
                        'ki.qualitaet.geaendertKopf'
                          | translate: { feld: ('ki.qualitaet.feld.' + aenderung.feld | translate) }
                      }}
                      <span class="tc-steps__ki-diff-spacer"></span>
                      @if (!readOnly()) {
                        <button
                          type="button"
                          class="tc-steps__ki-rueckgaengig"
                          data-testid="ki-rueckgaengig"
                          (click)="kiRueckgaengig(step.id, aenderung.ziel)"
                        >
                          {{ 'ki.qualitaet.rueckgaengig' | translate }}
                        </button>
                      }
                    </div>
                    <div class="tc-steps__ki-diff-zeile tc-steps__ki-diff-zeile--alt">
                      <span class="tc-steps__ki-diff-zeichen" aria-hidden="true">−</span>
                      <span class="sr-only">{{ 'ki.qualitaet.diffVorher' | translate }}</span>
                      <span class="tc-steps__ki-diff-alt">{{ aenderung.alt || '—' }}</span>
                    </div>
                    <div class="tc-steps__ki-diff-zeile tc-steps__ki-diff-zeile--neu">
                      <span class="tc-steps__ki-diff-zeichen" aria-hidden="true">+</span>
                      <span class="sr-only">{{ 'ki.qualitaet.diffNeu' | translate }}</span>
                      <span class="tc-steps__ki-diff-neu">{{ aenderung.neu }}</span>
                    </div>
                  </div>
                }
                @if (tcdlAll() && hasTcdl(step)) {
                  <div class="tc-steps__tcdl-block" [attr.data-testid]="'tcdl-row-collapse-' + step.id">
                    <button
                      type="button"
                      class="tc-steps__tcdl-toggle"
                      [class.tc-steps__tcdl-toggle--static]="!tcdlIsLong(step)"
                      [attr.aria-expanded]="tcdlIsLong(step) ? isTcdlExpanded(step.id) : null"
                      [disabled]="!tcdlIsLong(step)"
                      (click)="toggleTcdl(step.id)"
                    >
                      <span class="tc-steps__tcdl-toggle-kind">{{ 'testSteps.form.tcdlLabel' | translate }}</span>
                      <span class="tc-steps__tcdl-toggle-sep" aria-hidden="true">·</span>
                      <span class="tc-steps__tcdl-toggle-label">
                        @if (tcdlManualCount(step) > 0) {
                          {{
                            'testSteps.form.tcdlCollapseWithManual'
                              | translate: { count: tcdlInstructionCount(step), manual: tcdlManualCount(step) }
                          }}
                        } @else {
                          {{ 'testSteps.form.tcdlCollapse' | translate: { count: tcdlInstructionCount(step) } }}
                        }
                      </span>
                      @if (tcdlIsLong(step)) {
                        <mat-icon aria-hidden="true">
                          {{ isTcdlExpanded(step.id) ? 'expand_more' : 'chevron_right' }}
                        </mat-icon>
                      }
                    </button>
                    <div class="tc-steps__tcdl-list">
                      @for (line of tcdlVisibleLines(step); track $index) {
                        <div class="tc-steps__tcdl-line-row">
                          <span class="tc-steps__tcdl-line-num">{{ line.trim() ? $index + 1 : 'M' }}</span>
                          <code
                            class="tc-steps__tcdl-line-text"
                            [class.tc-steps__tcdl-line-text--manual]="!line.trim()"
                          >
                            {{ line.trim() || ('testSteps.form.tcdlManualLine' | translate) }}
                          </code>
                        </div>
                      }
                      @if (tcdlIsLong(step) && !isTcdlExpanded(step.id)) {
                        <button type="button" class="tc-steps__tcdl-more" (click)="toggleTcdl(step.id)">
                          {{ 'testSteps.form.tcdlMoreLines' | translate: { count: tcdlHiddenLineCount(step) } }}
                        </button>
                      }
                    </div>
                  </div>
                } @else if (tcdlAll()) {
                  <div class="tc-steps__tcdl-empty" [attr.data-testid]="'tcdl-empty-' + step.id">
                    <code class="tc-steps__tcdl-line-text tc-steps__tcdl-line-text--manual">
                      {{ 'testSteps.form.tcdlEmptyLine' | translate }}
                    </code>
                    @if (!readOnly()) {
                      <span class="tc-steps__tcdl-empty-sep" aria-hidden="true">·</span>
                      <button
                        type="button"
                        class="tc-steps__tcdl-more"
                        [attr.data-testid]="'tcdl-add-' + step.id"
                        [disabled]="busy()"
                        (click)="startEditTcdl(step)"
                      >
                        {{ 'testSteps.form.tcdlAddLink' | translate }}
                      </button>
                    }
                  </div>
                }
              </div>
              <div class="tc-steps__col tc-steps__col--link" role="cell">
                @if (step.notes) {
                  {{ step.notes }}
                } @else {
                  <span class="tc-steps__faint">—</span>
                }
              </div>
              <div class="tc-steps__col tc-steps__col--maintainer" role="cell">
                @if (maintainerName(step); as name) {
                  <app-avatar [name]="name" [size]="24" />
                  <span class="tc-steps__maintainer-name">{{ name }}</span>
                } @else {
                  <span class="tc-steps__faint">—</span>
                }
              </div>
              <div class="tc-steps__col tc-steps__col--actions" role="cell">
                <button
                  type="button"
                  class="tc-steps__action-btn tc-steps__action-btn--edit"
                  [attr.aria-label]="'testCases.form.testSteps.edit' | translate"
                  [matTooltip]="'testCases.form.testSteps.edit' | translate"
                  [disabled]="busy() || readOnly()"
                  (click)="startEdit(step)"
                >
                  <mat-icon aria-hidden="true">edit</mat-icon>
                </button>
                <button
                  type="button"
                  class="tc-steps__action-btn"
                  [attr.aria-label]="'testCases.form.testSteps.moveUp' | translate"
                  [matTooltip]="'testCases.form.testSteps.moveUp' | translate"
                  [disabled]="busy() || readOnly() || i === 0 || isDraft(step)"
                  (click)="moveUp(i)"
                >
                  <mat-icon aria-hidden="true">arrow_upward</mat-icon>
                </button>
                <button
                  type="button"
                  class="tc-steps__action-btn"
                  [attr.aria-label]="'testCases.form.testSteps.moveDown' | translate"
                  [matTooltip]="'testCases.form.testSteps.moveDown' | translate"
                  [disabled]="busy() || readOnly() || i === steps().length - 1 || isDraft(step)"
                  (click)="moveDown(i)"
                >
                  <mat-icon aria-hidden="true">arrow_downward</mat-icon>
                </button>
                <button
                  type="button"
                  class="tc-steps__action-btn tc-steps__action-btn--delete"
                  [attr.aria-label]="'testCases.form.testSteps.delete' | translate"
                  [matTooltip]="'testCases.form.testSteps.delete' | translate"
                  [disabled]="busy() || readOnly()"
                  (click)="delete(step)"
                >
                  <mat-icon aria-hidden="true">delete</mat-icon>
                </button>
              </div>
            </div>
          }
        }
      } @else {
        <div class="tc-steps__empty-row" role="row">
          <div class="tc-steps__empty" role="cell" aria-colspan="5">
            <p class="tc-steps__empty-title">{{ textKey('emptyTitle') | translate }}</p>
            <p class="tc-steps__empty-hint">{{ 'testCases.form.testSteps.emptyState.hint' | translate }}</p>
            <div class="tc-steps__empty-actions">
              <button
                mat-flat-button
                color="primary"
                type="button"
                class="tc-steps__empty-cta"
                [disabled]="busy() || readOnly()"
                (click)="startDraft()"
              >
                {{ 'testCases.form.testSteps.addFirstStep' | translate }}
              </button>
              @if (kontext() === 'modul') {
                <button
                  mat-stroked-button
                  type="button"
                  class="tc-steps__empty-library"
                  data-testid="tf-schritt-bibliothek-leer"
                  [disabled]="busy() || readOnly()"
                  (click)="openLibrary()"
                >
                  {{ textKey('library') | translate }}
                </button>
              }
            </div>
          </div>
        </div>
      }
    </div>
  }
</section>
